import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ALL_MEMBERS, ON_MEMBER_CHANGED } from '../../graphql/members';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import Constants from '../../utils/Constants';
import CheckboxField from '../fields/CheckboxField';
import Search from '../Search';
import { getValueMapping } from './MemberRow';
import MembersList from './MembersList';
import Paginator from '../Paginator';
import CreateMemberRow from './CreateMemberRow';
import {
    parseIncludeDeactivated,
    parsePaging,
    parseQuery,
    parseSorting,
} from '../../utils/queryString';
import { showInfoNotification } from '../../actions/notifications';
import Spinner from '../Spinner';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import SubscriptionActions from '../../utils/SubscriptionActions';
import Error from '../errors/Error';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';
import useAuthentication from '../../hooks/useAuthentication';

export function createNotificationText(t, action, name, memberId) {
    let notification;
    switch (action) {
        case SubscriptionActions.MEMBER_CREATED:
            notification = t('members.notification.member-create-thirdparty', { name, memberId });
            break;
        case SubscriptionActions.MEMBER_UPDATED:
            notification = t('members.notification.member-update-thirdparty', { name, memberId });
            break;
        default:
            notification = t('members.notification.members-modified-thirdparty');
    }
    return notification;
}

function Members() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const {
        sortKey = 'name',
        sortDirection = Constants.SORT_ASC,
    } = parseSorting(location.search);
    const query = parseQuery(location.search);
    const includeDeactivated = parseIncludeDeactivated(location.search);
    const dispatch = useDispatch();
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        query,
        includeDeactivated,
        sortDirection,
        sortKey,
    });
    const {
        loading,
        error,
        data,
        refetch,
    } = useExtendedQuery(ALL_MEMBERS, {
        variables: refetchProps,
        valueMappingFn: getValueMapping,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    const onSearch = (newQuery) => setRefetchProps({
        ...refetchProps,
        query: newQuery,
        page: 0,
    });
    const onFilter = (newIncludeDeactivated) => setRefetchProps({
        ...refetchProps,
        includeDeactivated: newIncludeDeactivated,
        page: 0,
    });
    useExtendedSubscription(ON_MEMBER_CHANGED, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'memberChanged',
        onChangeCallback: ({ action, model: { name, memberId } }) => {
            if (hasPrivilege(userPrivileges, [Privileges.MEMBER_EDIT])) {
                const notification = createNotificationText(t, action, name, memberId);
                dispatch(showInfoNotification(notification));
            }
        },
    });
    let result;
    let memberProps = null;
    let searchMatches = 0;
    if (loading) {
        result = <Spinner className="members-loading" />;
    } else if (error) {
        result = <Error className="members-error" error={error} />;
    } else {
        memberProps = {
            availableMemberTypes: data.allMembers.availableMemberTypes,
            availableProductTypes: data.allMembers.availableProductTypes,
            availablePaymentCurrencies: data.allMembers.availablePaymentCurrencies,
            availableZones: data.allMembers.availableZones,
            clearingEnabled: data.allMembers.clearingEnabled,
            euAuctioneersEnabled: data.allMembers.euAuctioneersEnabled,
        };
        searchMatches = data.allMembers.memberList.totalCount;
        result = (
            <Paginator
                totalCount={data.allMembers.memberList.totalCount}
                perPage={perPage}
                currentPage={refetchProps.page}
                onPageChange={onPageChange}
                bottom
            >
                <MembersList
                    members={data.allMembers.memberList.models}
                    memberProps={memberProps}
                    onSort={onSort}
                    sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                />
            </Paginator>
        );
    }
    return (
        <>
            <h4><FontAwesomeIcon icon="building" />&nbsp;{t('title.members')}</h4>
            {memberProps && <CreateMemberRow memberProps={memberProps} />}
            <div className="search-and-filter">
                <Search
                    placeholder={t('members.placeholder.search')}
                    query={refetchProps.query}
                    onSearch={onSearch}
                    matches={searchMatches}
                />
                <div className="filter">
                    <CheckboxField
                        label={t('members.include-deactivated')}
                        value={refetchProps.includeDeactivated}
                        onChange={onFilter}
                        className="members--search-and-filter--include-deactivated"
                        inputId="members--search-and-filter--include-deactivated"
                    />
                </div>
            </div>
            {result}
        </>
    );
}

export default Members;
